import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { MessageCircle } from 'lucide-react';

export default function DocsFAQ() {
  const faqs = [
    {
      question: 'Will this make us look bad if we have low activity?',
      answer: `Not at all. We design Proof to be honest but kind.

When activity is low, the widget:
• Either shows a "you might be early" style message
• Or can be configured to hide the status line below a minimum threshold

You decide:
• Whether to show views, purchases, or both
• What time windows we use (e.g., last 60 minutes for views, last 24 hours for purchases)

Our goal is not to embarrass your product. It's to create trust and transparency—and to give you a data stream you can use internally, even before you turn on the visible widget.`
    },
    {
      question: 'How do you calculate "views" and "purchases"?',
      answer: `We only use real events from your site.

**Views** are counted when:
• A visitor loads a product detail page (PDP) with our tracking snippet installed

**Purchases** are counted when:
• An order is completed and your thank-you/confirmation page calls our purchase event

We do not simulate or fake numbers. We don't scrape your analytics—you explicitly send us events via a tiny JS snippet (or server-side API), and we aggregate them into simple "last X minutes / last Y hours" counters.`
    },
    {
      question: 'What about privacy? Do you collect any personal data?',
      answer: `**No PII.**

We do not collect:
• Names
• Emails
• Addresses
• Payment details

We work with:
• Anonymous session IDs
• Hashed IPs (optional, for quality/dedupe)
• Product IDs, order IDs, basic metadata

You keep full control of your customer identity. We only use aggregated, anonymized activity to power Proof and Commerce Index signals.`
    },
    {
      question: 'Will this slow down our site?',
      answer: `The integration is designed to be lightweight and async.

• The CI script loads **asynchronously** and does not block your page rendering
• Events are sent via small background network calls (fetch/XHR), typically a few hundred bytes
• If our API is down or slow:
  - Your PDP still loads normally
  - The widget simply doesn't render Proof for that session

We care a lot about performance. If you want, we can test together in staging and measure any impact before you go live.`
    },
    {
      question: 'Can we control what is shown on the widget?',
      answer: `Yes, within sensible guardrails.

**You can configure:**
• Whether to show views, purchases, or both
• The time windows (e.g. last 15/30/60 minutes, last 12/24 hours)
• Thresholds for what counts as high / medium / low activity
• Whether to hide the "status message" below a certain activity threshold
• Choice of pre-approved copy variants that match your tone

**What you cannot change:**
• The underlying numbers (views/purchases) – they must come from real events
• The fact that time windows must be honest (we won't show 5-day-old stats as "live")

We're building Proof to be a trusted signal, not another gimmick.`
    },
    {
      question: 'What do we get beyond the widget?',
      answer: `The same event stream that powers Proof also powers:

• **Product-level activity insights** – views vs purchases over time
• **Category-level Commerce Index signals** – demand, momentum, pricing environment
• **Future benchmarks:**
  - "How does this product compare to others in your category on activity?"
  - "Is your category heating up or cooling down?"

So even if you start with just the live Proof widget, you're laying the data foundation for deeper CI insights later.`
    },
    {
      question: 'How hard is it to integrate?',
      answer: `Most brands can get the basics in **under 30 minutes**.

Your dev team needs to:
1. Add a small CI loader script to your layout
2. Call \`ci.trackView\` on PDPs
3. Call \`ci.trackPurchase\` on the order confirmation page

We'll give you cut-and-paste examples for Shopify, custom, or headless setups.`
    },
    {
      question: 'What happens if the CI endpoint is down?',
      answer: `Your site continues to work normally.

The ci.js script is designed to fail silently:
• Events are queued locally if the network request fails
• Failed events can be retried on the next page load
• The Proof widget simply won't display if data can't be fetched

Your checkout, product pages, and user experience are never blocked by CI.`
    },
    {
      question: 'Can we use our own product IDs?',
      answer: `Yes, absolutely.

Use whatever internal product identifiers make sense for your system. The key requirement is **consistency**:
• The \`product_id\` in view events must match the \`product_id\` in purchase events
• This is how we link "someone viewed X" to "someone bought X"

You can use Shopify product IDs, your own SKUs, or any stable identifier.`
    },
    {
      question: 'How often do view/purchase counts update in the extension?',
      answer: `Counts are updated in near real-time.

When the extension requests Proof data:
• Views are aggregated from events in the configured window (e.g., last 60 minutes)
• Purchases are aggregated from events in the configured window (e.g., last 24 hours)
• The aggregation happens on-demand, so data is always fresh

There's typically a 1-2 second delay from event receipt to availability in the API.`
    },
    {
      question: 'How do we test in staging vs production?',
      answer: `You can use the same \`brandId\` for both environments, or request separate IDs.

**Option 1: Shared ID**
• All events go to the same bucket
• Filter by URL patterns in your dashboard (if needed)

**Option 2: Separate IDs**
• Request a staging brandId (e.g., \`brand_xyz_staging\`)
• Production events stay isolated

Most brands start with Option 1 and graduate to Option 2 if needed.`
    }
  ];

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <Badge className="bg-[#FFD166]/20 text-[#FFD166] border-[#FFD166]/30 mb-4">
          Resources
        </Badge>
        <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Frequently Asked Questions
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Common questions about Commerce Index Proof integration.
        </p>
      </div>

      <Accordion type="single" collapsible className="space-y-4">
        {faqs.map((faq, index) => (
          <AccordionItem 
            key={index} 
            value={`item-${index}`}
            className="bg-[#0D1628] border border-[#22304A] rounded-lg px-6"
          >
            <AccordionTrigger className="text-white hover:text-[#3EF1C7] text-left py-4">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="text-[#94A3B8] pb-4 whitespace-pre-line">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Contact Card */}
      <Card className="bg-[#1E293B] border-[#334155] mt-12">
        <CardContent className="p-6 flex items-start gap-4">
          <MessageCircle className="w-8 h-8 text-[#3EF1C7] flex-shrink-0" />
          <div>
            <h3 className="text-white font-semibold mb-2">Still have questions?</h3>
            <p className="text-[#94A3B8] text-sm mb-4">
              Can't find what you're looking for? Our integration team is here to help.
            </p>
            <a 
              href="mailto:integrations@commerceindex.com"
              className="text-[#3EF1C7] hover:underline text-sm font-medium"
            >
              Contact integrations@commerceindex.com →
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
